import * as React from 'react';
import { CircleNotch, GitPullRequest } from '@phosphor-icons/react';
import { useSearch } from '@tanstack/react-router';
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import {
  ContentState,
  DashboardLayout,
  WorkspacePage,
  WorkspacePageHeader,
} from '@/components/layout';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import type { FindingsPrView, FindingsSearch } from '../findings-url';
import { getOpenPullRequestLoadMode } from '../findings-pr-model';
import type {
  IndexedPullRequest,
  OpenPullRequest,
  PrDecisionDetail,
  PrDecisionRow,
} from '../findings-types';
import { useFindingsUpdateSearch } from '../use-findings-update-search';
import { useFindingsScopeNav } from '../use-findings-scope-nav';
import { FindingsScopeSwitch } from '../findings-navigation';
import { PrPullRequestsIndex } from './pr-index';
import { PrDetailWorkbench } from './pr-detail-workbench';
import { normalizePrReviewView } from './pr-workbench-model';
import { usePrReviewActions } from './use-pr-review-actions';
import { useManualRescan } from '../repository/use-manual-rescan';
import { getQuota, type PlanId } from '../../../../../convex/billing/plans';
import s from '../findings.module.scss';

export function PrFindingsPage() {
  const search = useSearch({ strict: false }) as FindingsSearch;
  const updateSearch = useFindingsUpdateSearch();
  const { goToBacklog, goToPullRequests } = useFindingsScopeNav();
  const { org, orgId, isLoading: orgLoading } = useOrgContext();
  const {
    selectedProjectId,
    selectedProject,
    isLoading: projectLoading,
  } = useProjectContext();
  const [selectedDecisionKey, setSelectedDecisionKey] = React.useState<
    string | null
  >(null);
  const searchQuery = search.q ?? '';
  const activePrView: FindingsPrView = normalizePrReviewView(
    search.prView ?? 'needs-attention',
  );
  const [openPrsState, setOpenPrsState] = React.useState<{
    status: 'idle' | 'loading' | 'success' | 'error';
    items: OpenPullRequest[];
    // Hard failure (network/auth) — surfaced as a danger banner.
    error?: string;
    // Soft degradation (no installation, repo excluded) — surfaced as a
    // neutral notice while the page falls back to Fragments PR history.
    notice?: string;
  }>({ status: 'idle', items: [] });
  const orgPlan = (org?.plan ?? 'free') as PlanId;
  const isDemoOrg = Boolean((org as { isDemo?: boolean } | undefined)?.isDemo);
  const handleManualRescan = useManualRescan({
    orgId: org?._id,
    projectId: selectedProjectId,
    repoFullName: selectedProject?.repoFullName,
  });

  const prOptions = useQuery(
    api.findingsPr.getPullRequestOptions,
    selectedProjectId ? { projectId: selectedProjectId } : 'skip',
  );
  const activePrNumber = search.pr ?? null;
  const {
    actionState,
    handleCreatePrFix,
    handlePostPrComments,
    handleApproveDecision,
    handleRequestFixDecision,
  } = usePrReviewActions({
    selectedProjectId,
    orgId,
    activePrNumber,
    selectedDecisionKey,
  });
  React.useEffect(() => {
    const loadMode = getOpenPullRequestLoadMode({
      orgId,
      selectedProjectId,
      isDemoOrg,
    });
    if (loadMode.kind === 'blocked') return;
    if (loadMode.kind === 'demo') {
      setOpenPrsState({ status: 'success', items: [] });
      return;
    }
    const controller = new AbortController();
    setOpenPrsState((current) => ({
      status: 'loading',
      items: current.items,
    }));
    const params = new URLSearchParams({
      orgId: loadMode.orgId,
      projectId: loadMode.projectId,
    });
    void fetch(`/api/github/open-prs?${params.toString()}`, {
      method: 'GET',
      signal: controller.signal,
    })
      .then(async (response) => {
        const data = (await response.json().catch(() => ({}))) as {
          pullRequests?: OpenPullRequest[];
          warning?: string;
          error?: string;
        };
        if (!response.ok) {
          throw new Error(data.error ?? 'Could not load open pull requests.');
        }
        if (data.warning) {
          setOpenPrsState({
            status: 'success',
            items: [],
            notice:
              'GitHub pull requests are unavailable; showing Fragments PR history instead.',
          });
        } else {
          setOpenPrsState({
            status: 'success',
            items: data.pullRequests ?? [],
          });
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setOpenPrsState({
          status: 'error',
          items: [],
          error:
            error instanceof Error
              ? error.message
              : 'Could not load open pull requests.',
        });
      });
    return () => controller.abort();
  }, [isDemoOrg, orgId, selectedProjectId]);

  const summary = useQuery(
    api.findingsPr.getPrSummary,
    selectedProjectId && activePrNumber
      ? {
          projectId: selectedProjectId,
          prNumber: activePrNumber,
          view: activePrView,
          searchQuery: searchQuery || undefined,
        }
      : 'skip',
  );
  const decisionsResult = useQuery(
    api.findingsPr.getPrDecisions,
    selectedProjectId && activePrNumber
      ? {
          projectId: selectedProjectId,
          prNumber: activePrNumber,
          view: activePrView,
          searchQuery: searchQuery || undefined,
          limit: 100,
        }
      : 'skip',
  );
  const detail = useQuery(
    api.findingsPr.getPrDecisionDetail,
    selectedProjectId && activePrNumber && selectedDecisionKey
      ? {
          projectId: selectedProjectId,
          prNumber: activePrNumber,
          decisionKey: selectedDecisionKey,
        }
      : 'skip',
  ) as PrDecisionDetail | null | undefined;
  const usageSummary = useQuery(
    api.billing.usage.getUsageSummary,
    orgId && !isDemoOrg ? { orgId } : 'skip',
  );

  const decisions = React.useMemo(
    () => (decisionsResult?.decisions ?? []) as PrDecisionRow[],
    [decisionsResult],
  );
  const prRunsByNumber = React.useMemo(() => {
    const byNumber = new Map<number, NonNullable<typeof prOptions>[number]>();
    for (const option of prOptions ?? []) byNumber.set(option.prNumber, option);
    return byNumber;
  }, [prOptions]);
  const fallbackPullRequests = React.useMemo<OpenPullRequest[]>(
    () =>
      (prOptions ?? []).map((option) => ({
        prNumber: option.prNumber,
        title: `PR #${option.prNumber}`,
        branch: option.branch ?? 'Unknown branch',
        baseBranch: selectedProject?.defaultBranch ?? 'default',
        author: option.prAuthor ?? null,
        url:
          option.repoFullName && option.prNumber
            ? `https://github.com/${option.repoFullName}/pull/${option.prNumber}`
            : '#',
        state: 'closed',
        mergedAt: null,
        draft: false,
        headSha: '',
        createdAt: new Date(option.capturedAt).toISOString(),
        updatedAt: new Date(option.capturedAt).toISOString(),
      })),
    [prOptions, selectedProject?.defaultBranch],
  );
  const allPullRequests = React.useMemo<IndexedPullRequest[]>(() => {
    const byNumber = new Map<number, IndexedPullRequest>();
    if (openPrsState.status === 'success') {
      for (const pullRequest of openPrsState.items) {
        byNumber.set(pullRequest.prNumber, {
          ...pullRequest,
          run: prRunsByNumber.get(pullRequest.prNumber),
        });
      }
    }
    for (const pullRequest of fallbackPullRequests) {
      if (byNumber.has(pullRequest.prNumber)) continue;
      byNumber.set(pullRequest.prNumber, {
        ...pullRequest,
        run: prRunsByNumber.get(pullRequest.prNumber),
      });
    }
    return Array.from(byNumber.values()).sort((a, b) => {
      const aUpdated = new Date(a.updatedAt).getTime();
      const bUpdated = new Date(b.updatedAt).getTime();
      return (
        (Number.isFinite(bUpdated) ? bUpdated : 0) -
        (Number.isFinite(aUpdated) ? aUpdated : 0)
      );
    });
  }, [fallbackPullRequests, openPrsState, prRunsByNumber]);
  const activePullRequest = React.useMemo(
    () =>
      activePrNumber
        ? allPullRequests.find(
            (pullRequest) => pullRequest.prNumber === activePrNumber,
          )
        : undefined,
    [activePrNumber, allPullRequests],
  );
  const isLoading =
    orgLoading ||
    projectLoading ||
    (prOptions === undefined && !activePrNumber) ||
    (!activePrNumber && openPrsState.status === 'loading') ||
    (!activePrNumber && openPrsState.status === 'idle') ||
    (!!activePrNumber &&
      (summary === undefined || decisionsResult === undefined));

  const autofixUsage = React.useMemo(() => {
    const meter = usageSummary?.find((item) => item.quota === 'maxAutofixPrs');
    return {
      current: meter?.current ?? 0,
      limit: meter?.limit ?? getQuota(orgPlan, 'maxAutofixPrs'),
    };
  }, [orgPlan, usageSummary]);
  React.useEffect(() => {
    if (decisions.length === 0) {
      if (selectedDecisionKey) setSelectedDecisionKey(null);
      return;
    }
    if (!selectedDecisionKey) {
      setSelectedDecisionKey(decisions[0].key);
      return;
    }
    if (!decisions.some((decision) => decision.key === selectedDecisionKey)) {
      setSelectedDecisionKey(decisions[0].key);
    }
  }, [decisions, selectedDecisionKey]);

  return (
    <DashboardLayout activeNav="Pull Requests" flush>
      <WorkspacePage className={s.contentPanel}>
        <WorkspacePageHeader
          title="Findings"
          description={
            selectedProject?.name
              ? `Review pull request governance findings in ${selectedProject.name}.`
              : 'Review pull request governance findings.'
          }
        />

        {!activePrNumber && (
          <FindingsScopeSwitch
            activeScope="pr"
            onRepository={goToBacklog}
            onPullRequests={goToPullRequests}
          />
        )}

        {!activePrNumber ? (
          isLoading ? (
            <div className={s.loadingState}>
              <CircleNotch size={18} className={s.spinIcon} />
              Loading open pull requests&hellip;
            </div>
          ) : (
            <PrPullRequestsIndex
              rows={allPullRequests}
              error={
                openPrsState.status === 'error'
                  ? (openPrsState.error ?? null)
                  : null
              }
              notice={openPrsState.notice ?? null}
              onSelect={(prNumber) =>
                updateSearch({
                  pr: prNumber,
                  prView: undefined,
                  q: undefined,
                })
              }
              onRepository={goToBacklog}
            />
          )
        ) : (
          <>
            {isLoading ? (
              <div className={s.loadingState}>
                <CircleNotch size={18} className={s.spinIcon} />
                Loading PR findings&hellip;
              </div>
            ) : !summary ? (
              <ContentState
                className={s.emptyState}
                icon={<GitPullRequest size={40} />}
                title={`No findings for PR #${activePrNumber}`}
                description="Fragments has not seen governance findings for the selected pull request."
                action={
                  <button
                    type="button"
                    className={s.prPrimaryButton}
                    onClick={() =>
                      updateSearch({
                        pr: undefined,
                        prView: undefined,
                        q: undefined,
                      })
                    }
                  >
                    View open pull requests
                  </button>
                }
                minHeight={320}
              />
            ) : (
              <PrDetailWorkbench
                prNumber={activePrNumber}
                repoFullName={
                  summary.repoFullName ?? selectedProject?.repoFullName
                }
                pullRequest={activePullRequest}
                summary={summary}
                decisions={decisions}
                selectedDecisionKey={selectedDecisionKey}
                detail={detail}
                activePrView={activePrView}
                searchQuery={searchQuery}
                plan={orgPlan}
                readOnly={isDemoOrg}
                autofixUsage={autofixUsage}
                actionState={actionState}
                onBack={() =>
                  updateSearch({
                    pr: undefined,
                    prView: undefined,
                    q: undefined,
                  })
                }
                onViewChange={(view) =>
                  updateSearch({
                    prView: view === 'needs-attention' ? undefined : view,
                  })
                }
                onSearchChange={(value) =>
                  updateSearch({ q: value || undefined })
                }
                onSelectDecision={setSelectedDecisionKey}
                onCreateFix={handleCreatePrFix}
                onPostComments={handlePostPrComments}
                onApprove={handleApproveDecision}
                onRequestFix={handleRequestFixDecision}
                onRerun={handleManualRescan}
              />
            )}
          </>
        )}
      </WorkspacePage>
    </DashboardLayout>
  );
}
